import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisFilterManagerComponent,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR,
	TableEntityField,
} from '@campaign-portal/components-library';
import { FilterType, Paging } from '@campaign-portal/namespace/common/rpc.params';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { CamaraRulesService } from './camara-rules.service';
import { CamaraRulesFieldsService } from './camara-rules.fields.service';
import { EditCamaraRuleComponent } from './edit-camara-rule/edit-camara-rule.component';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../auth/user.service';
import { EntityField, InputComplexType, InputType } from '@campaign-portal/namespace/common/entityField';
import { ClientsService } from '../clients/clients.service';
import { SourcesService } from '../sources/sources.service';
import { CamaraRule, OAUTH_TYPES } from '@helpers/types/models';
import { CamaraRuleDeleteDialogComponent } from './dialog/dialog.component';
import { UsersService } from '../users/users.service';
import { ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Client, Source, User } from '@helpers/types/models';
import { ChangeDetectorRef, inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

enum FilterVariables {
	ID = 'id',
	RULE_TYPE = 'ruleType',
	CLIENT = 'clientList',
	PREFIX = 'prefixList',
	SOURCE = 'source',
	CREATED_BY = 'createdBy',
	LAST_UPDATED = 'lastUpdated',
}

const idField = new TableEntityField({
	id: null,
	name: 'camara_rules.id',
	variable: FilterVariables.ID,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT,
});
const ruleTypeField = new TableEntityField({
	id: null,
	name: 'camara_rules.rule_type',
	variable: FilterVariables.RULE_TYPE,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: [],
});
const clientField = new TableEntityField({
	id: null,
	name: 'camara_rules.client',
	variable: FilterVariables.CLIENT,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: [],
});
const prefixField = new TableEntityField({
	id: null,
	name: 'camara_rules.prefix',
	variable: FilterVariables.PREFIX,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT,
});
const sourceField = new TableEntityField({
	id: null,
	name: 'camara_rules.source',
	variable: FilterVariables.SOURCE,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: [],
});
const createdByField = new TableEntityField({
	id: null,
	name: 'camara_rules.created_by',
	variable: FilterVariables.CREATED_BY,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: [],
});
const lastUpdatedField = new TableEntityField({
	id: null,
	name: 'camara_rules.last_updated',
	variable: FilterVariables.LAST_UPDATED,
	filterType: FilterType.BETWEEN,
	required: false,
	type: InputComplexType.DATE_RANGE,
});

@Component({
	selector: 'app-camara-rules',
	templateUrl: './camara-rules.component.html',
	styleUrls: [
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'./camara-rules.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService },
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CamaraRulesComponent extends AlarisComplexTableComponent<CamaraRule> implements OnDestroy, AfterViewInit {
	private headers: EntityField[] = [];
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (): boolean => !this.user.allowed(['camara_rules.edit']),
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => !this.user.allowed(['camara_rules.edit']),
		},
	];
	override readonly dblClickRowAction = this.edit;
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize,
	};
	readonly exporting$ = new BehaviorSubject<boolean>(false);

	clients: { id: number; name: string }[] = [];
	sourceOptions: string[] = [];
	userOptions: { id: number; name: string }[] = [];
	ruleTypeOptions = OAUTH_TYPES;

	private cdr = inject(ChangeDetectorRef);

	constructor(
		dataService: CamaraRulesService,
		public override readonly fieldsService: CamaraRulesFieldsService,
		tableSettingsService: TableSettingsService,
		private readonly dialog: AlarisDialogService,
		private readonly rulesService: CamaraRulesService,
		protected readonly user: UserService,
		private readonly clientsService: ClientsService,
		private readonly sourcesService: SourcesService,
		private readonly usersService: UsersService,
	) {
		super(dataService, fieldsService, tableSettingsService, TableTypes.INTERNAL);

		this.headers = [idField, ruleTypeField, clientField, prefixField, sourceField, createdByField, lastUpdatedField];
		this.state
			.filters()
			.set(FilterVariables.ID, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.RULE_TYPE, { enabled: true, value: [], filterType: FilterType.LIKE })
			.set(FilterVariables.CLIENT, { enabled: true, value: [], filterType: FilterType.LIKE })
			.set(FilterVariables.PREFIX, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.SOURCE, { enabled: true, value: [], filterType: FilterType.LIKE })
			.set(FilterVariables.CREATED_BY, { enabled: true, value: [], filterType: FilterType.LIKE })
			.set(FilterVariables.LAST_UPDATED, { enabled: true, filterType: FilterType.BETWEEN });
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.exporting$.next(false);
		this.exporting$.complete();
	}

	override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		ruleTypeField.data = this.ruleTypeOptions;
		this.setRefBooksInFields({
			ruleType: { list$: new BehaviorSubject(this.ruleTypeOptions) }
		});
		this.loadFilterOptions();
	}

	private loadFilterOptions(): void {
		this.clientsService.read({ offset: 0, limit: 1000 })
			.pipe(
				catchError(err => {
					console.error('Clients error:', err);
					return of({ Success: true, Total: 0, Data: [] } as ReadResponse<Client[]>);
				})
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((res: ReadResponse<Client[]>) => {
				this.clients = (res.Data || [])
					.filter(c => c.id !== null)
					.map(c => ({ id: c.id as number, name: c.name ?? '' }));
				clientField.data = this.clients;
				this.setRefBooksInFields({ clientList: { list$: new BehaviorSubject(this.clients) } });
				this.cdr.markForCheck();
			});

		this.sourcesService.load()
			.pipe(
				catchError(err => {
					console.error('Sources error:', err);
					return of({ Success: true, Total: 0, Data: [] } as ReadResponse<Source<number>[]>);
				})
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((res: ReadResponse<Source<number>[]>) => {
				this.sourceOptions = (res.Data || []).map(s => s.Name);
				sourceField.data = this.sourceOptions;
				this.setRefBooksInFields({ source: { list$: new BehaviorSubject(this.sourceOptions) } });
				this.cdr.markForCheck();
			});

		this.usersService.read({ offset: 0, limit: 1000 })
			.pipe(
				catchError(err => {
					console.error('Users error:', err);
					return of({ Success: true, Total: 0, Data: [] } as ReadResponse<User[]>);
				})
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((res: ReadResponse<User[]>) => {
				this.userOptions = (res.Data || [])
					.filter(u => u.ID !== null)
					.map(u => ({ id: u.ID as number, name: u.Login ?? '' }));
				createdByField.data = this.userOptions;
				this.setRefBooksInFields({ createdBy: { list$: new BehaviorSubject(this.userOptions) } });
				this.cdr.markForCheck();
			});
	}

	getClientNames(clientIds: number[]): string {
		if (!clientIds || !clientIds.length) return '';
		return this.clients
			.filter(c => clientIds.includes(c.id))
			.map(c => c.name)
			.join(', ');
	}

	getRuleTypeLabel(value: string | null): string {
		if (!value) return '-';
		const found = this.ruleTypeOptions.find(opt => opt.value === value);
		return found ? found.name : value;
	}

	edit(rule?: CamaraRule): void {
		this.editPanel.open<EditCamaraRuleComponent, { rule: CamaraRule }, boolean>(
			EditCamaraRuleComponent,
			EditPanelWidth.MD,
			{ rule: rule ? { ...rule } : { id: null, ruleType: null, prefixList: [], clientList: [], source: '' } }
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(result => {
				if (result) this.load();
			});
	}

	delete(rule: CamaraRule): void {
		this.dialog
			.open<boolean>(CamaraRuleDeleteDialogComponent, { autoFocus: false, data: { rule } })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(result => {
				if (result) this.load();
			});
	}

	export(): void {
		this.exporting$.next(true);
		this.rulesService
			.export(this.total, this.state.readParams)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.exporting$.next(false));
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.state.filters,
			tHeaders: this.headers,
		});
	}

	formatDate(inputDate: string | undefined): string {
		if (inputDate) {
			const date = new Date(inputDate);
			const pad = (num: number): string => num.toString().padStart(2, '0');

			const day = pad(date.getDate());
			const month = pad(date.getMonth() + 1);
			const year = date.getFullYear();

			const hours = pad(date.getHours());
			const minutes = pad(date.getMinutes());
			const seconds = pad(date.getSeconds());

			return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
		} else {
			return '';
		}
	}
}